import { useMemo, useState } from "react";
import type { ShopifyProduct } from "@/lib/shopify";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SlidersHorizontal, X } from "lucide-react";
import { colorToHex } from "@/lib/colorMap";

export interface FilterState {
  inStockOnly: boolean;
  colors: string[];
  sizes: string[];
  priceRange: [number, number];
}

export function deriveFilterFacets(products: ShopifyProduct[]) {
  const colorSet = new Set<string>();
  const sizeSet = new Set<string>();
  let minPrice = Infinity;
  let maxPrice = 0;
  for (const p of products) {
    const price = parseFloat(p.node.priceRange.minVariantPrice.amount);
    if (price < minPrice) minPrice = price;
    if (price > maxPrice) maxPrice = price;
    for (const opt of p.node.options ?? []) {
      const name = opt.name.toLowerCase();
      if (name === "color") opt.values.forEach(v => colorSet.add(v));
      if (name === "size") opt.values.forEach(v => sizeSet.add(v));
    }
  }
  if (!isFinite(minPrice)) minPrice = 0;
  if (maxPrice === 0) maxPrice = 100;
  return {
    colors: Array.from(colorSet).sort(),
    sizes: Array.from(sizeSet),
    priceMin: Math.floor(minPrice),
    priceMax: Math.ceil(maxPrice),
  };
}

export function applyFilters(products: ShopifyProduct[], filters: FilterState): ShopifyProduct[] {
  return products.filter((p) => {
    const price = parseFloat(p.node.priceRange.minVariantPrice.amount);
    if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;

    if (filters.inStockOnly) {
      const anyAvailable = p.node.variants.edges.some(v => v.node.availableForSale);
      if (!anyAvailable) return false;
    }

    if (filters.colors.length > 0) {
      const productColors = p.node.options?.find(o => o.name.toLowerCase() === "color")?.values ?? [];
      if (!productColors.some(c => filters.colors.includes(c))) return false;
    }

    if (filters.sizes.length > 0) {
      const productSizes = p.node.options?.find(o => o.name.toLowerCase() === "size")?.values ?? [];
      if (!productSizes.some(s => filters.sizes.includes(s))) return false;
    }

    return true;
  });
}

interface Props {
  facets: ReturnType<typeof deriveFilterFacets>;
  filters: FilterState;
  setFilters: (f: FilterState) => void;
  resultCount: number;
}

function FilterBody({ facets, filters, setFilters }: Omit<Props, "resultCount">) {
  const toggleColor = (color: string) => {
    setFilters({
      ...filters,
      colors: filters.colors.includes(color) ? filters.colors.filter(c => c !== color) : [...filters.colors, color],
    });
  };
  const toggleSize = (size: string) => {
    setFilters({
      ...filters,
      sizes: filters.sizes.includes(size) ? filters.sizes.filter(s => s !== size) : [...filters.sizes, size],
    });
  };

  return (
    <div className="space-y-1">
      {/* In stock */}
      <label className="flex cursor-pointer items-center justify-between border-b border-[#D4CCBC] py-4">
        <span className="font-ui text-xs font-bold uppercase tracking-[0.15em] text-[#2A1510]">In Stock Only</span>
        <Checkbox
          checked={filters.inStockOnly}
          onCheckedChange={(v) => setFilters({ ...filters, inStockOnly: !!v })}
        />
      </label>

      <Accordion type="multiple" defaultValue={["color", "size", "price"]} className="space-y-0">
        {facets.colors.length > 0 && (
          <AccordionItem value="color" className="border-b border-[#D4CCBC]">
            <AccordionTrigger className="font-ui text-xs font-bold uppercase tracking-[0.15em] text-[#2A1510] hover:no-underline">
              Color
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-4 gap-2 pt-2">
                {facets.colors.map((c) => {
                  const selected = filters.colors.includes(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleColor(c)}
                      title={c}
                      className={`flex flex-col items-center gap-1 rounded-sm p-1.5 transition-colors ${selected ? "bg-[#FA5021]/10" : "hover:bg-[#F5EFE2]"}`}
                    >
                      <span
                        className={`h-7 w-7 rounded-full border-2 ${selected ? "border-[#FA5021]" : "border-[#D4CCBC]"}`}
                        style={{ backgroundColor: colorToHex(c) }}
                      />
                      <span className="font-ui text-[9px] font-semibold uppercase tracking-wider text-[#2A1510]/90 line-clamp-1">
                        {c}
                      </span>
                    </button>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {facets.sizes.length > 0 && (
          <AccordionItem value="size" className="border-b border-[#D4CCBC]">
            <AccordionTrigger className="font-ui text-xs font-bold uppercase tracking-[0.15em] text-[#2A1510] hover:no-underline">
              Size
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-wrap gap-2 pt-2">
                {facets.sizes.map((s) => {
                  const selected = filters.sizes.includes(s);
                  return (
                    <button
                      key={s}
                      onClick={() => toggleSize(s)}
                      className={`min-w-[44px] rounded-sm border px-3 py-1.5 font-ui text-xs font-semibold uppercase tracking-wider transition-colors ${selected ? "border-[#FA5021] bg-[#FA5021]/10 text-[#2A1510]" : "border-[#D4CCBC] text-[#2A1510]/90 hover:border-[#2A1510]"}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem value="price" className="border-b border-[#D4CCBC]">
          <AccordionTrigger className="font-ui text-xs font-bold uppercase tracking-[0.15em] text-[#2A1510] hover:no-underline">
            Price
          </AccordionTrigger>
          <AccordionContent>
            <div className="px-1 pt-4">
              <Slider
                min={facets.priceMin}
                max={facets.priceMax}
                step={1}
                value={filters.priceRange}
                onValueChange={(v) => setFilters({ ...filters, priceRange: [v[0], v[1]] as [number, number] })}
              />
              <div className="mt-3 flex justify-between font-ui text-xs text-[#2A1510]">
                <span>${filters.priceRange[0]}</span>
                <span>${filters.priceRange[1]}</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export function ProductFilters(props: Props) {
  const { facets, filters, setFilters } = props;
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeCount =
    (filters.inStockOnly ? 1 : 0) +
    filters.colors.length +
    filters.sizes.length +
    (filters.priceRange[0] > facets.priceMin || filters.priceRange[1] < facets.priceMax ? 1 : 0);

  const clearAll = () =>
    setFilters({
      inStockOnly: false,
      colors: [],
      sizes: [],
      priceRange: [facets.priceMin, facets.priceMax],
    });

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block">
        <div className="sticky top-24">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#2A1510]">Filter</h3>
            {activeCount > 0 && (
              <button onClick={clearAll} className="font-ui text-[11px] font-semibold uppercase tracking-wider text-[#FA5021] hover:underline">
                Clear ({activeCount})
              </button>
            )}
          </div>
          <FilterBody {...props} />
        </div>
      </aside>

      {/* Mobile trigger */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <button className="flex items-center gap-2 rounded-sm border border-[#D4CCBC] bg-[#FDFAF3] px-4 py-2 font-ui text-xs font-bold uppercase tracking-[0.15em] text-[#2A1510] lg:hidden">
            <SlidersHorizontal className="h-4 w-4" />
            Filter
            {activeCount > 0 && (
              <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FA5021] text-[10px] text-[#F5EFE2]">{activeCount}</span>
            )}
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-full max-w-sm overflow-y-auto bg-[#FDFAF3]">
          <SheetHeader>
            <SheetTitle className="font-heading uppercase tracking-[0.2em] text-[#2A1510]">Filter</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <FilterBody {...props} />
            <div className="mt-6 flex gap-2">
              <button onClick={clearAll} className="flex-1 rounded-sm border border-[#D4CCBC] py-3 font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#2A1510]">
                Clear
              </button>
              <button onClick={() => setMobileOpen(false)} className="flex-1 rounded-sm bg-[#FA5021] py-3 font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#F5EFE2]">
                Apply
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

interface ChipsProps {
  filters: FilterState;
  facets: ReturnType<typeof deriveFilterFacets>;
  setFilters: (f: FilterState) => void;
}

export function ActiveFilterChips({ filters, facets, setFilters }: ChipsProps) {
  const chips: { label: string; onRemove: () => void }[] = [];
  if (filters.inStockOnly) chips.push({ label: "In stock", onRemove: () => setFilters({ ...filters, inStockOnly: false }) });
  filters.colors.forEach(c => chips.push({ label: c, onRemove: () => setFilters({ ...filters, colors: filters.colors.filter(x => x !== c) }) }));
  filters.sizes.forEach(s => chips.push({ label: `Size ${s}`, onRemove: () => setFilters({ ...filters, sizes: filters.sizes.filter(x => x !== s) }) }));
  if (filters.priceRange[0] > facets.priceMin || filters.priceRange[1] < facets.priceMax) {
    chips.push({
      label: `$${filters.priceRange[0]} – $${filters.priceRange[1]}`,
      onRemove: () => setFilters({ ...filters, priceRange: [facets.priceMin, facets.priceMax] }),
    });
  }
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip, i) => (
        <button
          key={i}
          onClick={chip.onRemove}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#D4CCBC] bg-[#FDFAF3] px-3 py-1 font-ui text-[11px] font-semibold uppercase tracking-wider text-[#2A1510] hover:border-[#FA5021] hover:text-[#FA5021]"
        >
          {chip.label}
          <X className="h-3 w-3" />
        </button>
      ))}
    </div>
  );
}

export type SortKey = "featured" | "price-asc" | "price-desc" | "title-asc";

export function applySort(products: ShopifyProduct[], sort: SortKey): ShopifyProduct[] {
  const arr = [...products];
  switch (sort) {
    case "price-asc":
      return arr.sort((a, b) => parseFloat(a.node.priceRange.minVariantPrice.amount) - parseFloat(b.node.priceRange.minVariantPrice.amount));
    case "price-desc":
      return arr.sort((a, b) => parseFloat(b.node.priceRange.minVariantPrice.amount) - parseFloat(a.node.priceRange.minVariantPrice.amount));
    case "title-asc":
      return arr.sort((a, b) => a.node.title.localeCompare(b.node.title));
    default:
      return arr;
  }
}

export function useDefaultFilters(facets: ReturnType<typeof deriveFilterFacets>): FilterState {
  return useMemo(
    () => ({
      inStockOnly: false,
      colors: [],
      sizes: [],
      priceRange: [facets.priceMin, facets.priceMax] as [number, number],
    }),
    [facets.priceMin, facets.priceMax]
  );
}
